export interface ThinkingLeakSegment {
  type: 'text' | 'thinking';
  content: string;
  truncated?: boolean;
  recovered?: boolean;
}

interface ProtectedSpan {
  protected: boolean;
  content: string;
}

const INTERNAL_THINKING_TAGS = 'think|thinking|analysis';
const THINK_TAG_RE = new RegExp(`</?\\s*(?:${INTERNAL_THINKING_TAGS})\\b[^>]*>`, 'gi');
const THINK_CLOSE_RE = new RegExp(`</\\s*(?:${INTERNAL_THINKING_TAGS})\\s*>`, 'gi');
const THINK_OPEN_RE = new RegExp(`<\\s*(?:${INTERNAL_THINKING_TAGS})\\b[^>]*>`, 'i');

/** 保护 Markdown 代码示例，防止代码中的标签被误删。 */
function splitMarkdownProtectedSpans(text: string): ProtectedSpan[] {
  const spans: ProtectedSpan[] = [];
  let cursor = 0;

  const push = (content: string, protectedSpan: boolean) => {
    if (!content) return;
    const last = spans[spans.length - 1];
    if (last && last.protected === protectedSpan) {
      last.content += content;
    } else {
      spans.push({ protected: protectedSpan, content });
    }
  };

  while (cursor < text.length) {
    if (text.startsWith('```', cursor) || text.startsWith('~~~', cursor)) {
      const fence = text.slice(cursor, cursor + 3);
      const close = text.indexOf(fence, cursor + 3);
      if (close === -1) {
        push(text.slice(cursor), true);
        break;
      }
      push(text.slice(cursor, close + 3), true);
      cursor = close + 3;
      continue;
    }

    if (text[cursor] === '`') {
      let tickCount = 1;
      while (text[cursor + tickCount] === '`') tickCount += 1;
      const fence = '`'.repeat(tickCount);
      const close = text.indexOf(fence, cursor + tickCount);
      if (close !== -1) {
        push(text.slice(cursor, close + tickCount), true);
        cursor = close + tickCount;
        continue;
      }
    }

    const nextFence = (() => {
      const candidates = ['```', '~~~', '`']
        .map((token) => text.indexOf(token, cursor + 1))
        .filter((idx) => idx !== -1);
      return candidates.length > 0 ? Math.min(...candidates) : text.length;
    })();
    push(text.slice(cursor, nextFence), false);
    cursor = nextFence;
  }

  return spans;
}

/** 拆分显式思考标签，流式未闭合片段保持思考状态。 */
function parseRawThinkingSegments(text: string): ThinkingLeakSegment[] {
  const segments: ThinkingLeakSegment[] = [];
  let cursor = 0;
  THINK_TAG_RE.lastIndex = 0;

  const pushText = (content: string) => {
    if (!content) return;
    const last = segments[segments.length - 1];
    if (last?.type === 'text') last.content += content;
    else segments.push({ type: 'text', content });
  };

  while (cursor < text.length) {
    THINK_TAG_RE.lastIndex = cursor;
    const tag = THINK_TAG_RE.exec(text);
    if (!tag) {
      pushText(text.slice(cursor));
      break;
    }

    const tagText = tag[0];
    const tagStart = tag.index;
    const tagEnd = tag.index + tagText.length;
    const isOpen = THINK_OPEN_RE.test(tagText) && !tagText.startsWith('</');

    if (!isOpen) {
      // 孤立结束标签不提供可靠边界，保留正文，不按语言或关键词猜测思考内容。
      pushText(text.slice(cursor, tagStart));
      pushText(text.slice(tagEnd));
      break;
    }

    pushText(text.slice(cursor, tagStart));
    THINK_CLOSE_RE.lastIndex = tagEnd;
    const close = THINK_CLOSE_RE.exec(text);
    if (!close) {
      segments.push({
        type: 'thinking',
        content: text.slice(tagEnd),
        truncated: true,
      });
      break;
    }

    segments.push({
      type: 'thinking',
      content: text.slice(tagEnd, close.index),
    });
    cursor = close.index + close[0].length;
  }

  return segments;
}

/** 将模型正文分成公开文本和显式思考片段，不修改原始消息。 */
export function splitThinkingLeakSegments(text: string): ThinkingLeakSegment[] {
  const spans = splitMarkdownProtectedSpans(text);
  const segments: ThinkingLeakSegment[] = [];

  const push = (segment: ThinkingLeakSegment) => {
    if (!segment.content) return;
    const last = segments[segments.length - 1];
    if (
      last?.type === segment.type &&
      last.truncated === segment.truncated &&
      last.recovered === segment.recovered
    ) {
      last.content += segment.content;
    } else {
      segments.push(segment);
    }
  };

  for (const span of spans) {
    if (span.protected) {
      push({ type: 'text', content: span.content });
    } else {
      for (const segment of parseRawThinkingSegments(span.content)) {
        push(segment);
      }
    }
  }

  return segments;
}

/** 判断正文是否含有可识别的思考片段。 */
export function hasThinkingLeak(text: string): boolean {
  return splitThinkingLeakSegments(text).some((segment) => segment.type === 'thinking');
}

/** 复制及展示公开正文时去掉思考片段，保留代码示例。 */
export function stripThinkingLeak(text: string): string {
  return splitThinkingLeakSegments(text)
    .filter((segment) => segment.type === 'text')
    .map((segment) => segment.content)
    .join('')
    .trim();
}
